import clsx from 'clsx';
import { observer } from 'mobx-react-lite';
import { CSSProperties, ReactNode } from 'react';
import classes from './DataGrid.module.css';
import formats from './formats';
import OrderButton from './OrderButton';
import { Column, Record } from './types';

export default observer(function DataGrid<T extends Record>({
  className,
  columns: columnsConfig,
  data,
  orderBy,
  idField = 'id',
  onOrderByChange,
}: {
  className?: string;
  columns: Column<T>[];
  data: T[];
  idField?: keyof T;
  onOrderByChange?: (field: string) => void;
  orderBy?: string;
}) {
  const columns = columnsFromConfig<T>(columnsConfig);

  const normalizedData = Array.isArray(data) ? data : [];

  return (
    <table className={clsx(classes.table, className)}>
      <thead>
        <tr>
          {columns.map((column) =>
            renderHeader(column as Column<any>, data, orderBy, onOrderByChange),
          )}
        </tr>
      </thead>
      <tbody>
        {normalizedData.map((row) =>
          renderRow(row, columns, row[idField] as string),
        )}
      </tbody>
    </table>
  );
});

function renderHeader<T extends Record>(
  column: Column,
  data: T[],
  orderBy?: string,
  onOrderByChange?: (field: string) => void,
) {
  const content = column.renderHeader?.(column, data) || column.header;
  if ((content as any)?.type === 'th') {
    return content;
  }

  return (
    <th
      className={clsx(
        classes.headerCell,
        column.classes,
        column.noStretch && classes.noStretch,
      )}
      key={column.id}
      style={getStyleFromColumn(column)}
    >
      <OrderButton
        column={column}
        onOrderByChange={onOrderByChange}
        orderBy={orderBy}
      >
        {content}
      </OrderButton>
    </th>
  );
}

function renderRow<T extends Record>(
  record: T,
  columns: Column<T>[],
  key: string,
) {
  return (
    <tr key={key}>{columns.map((column) => renderCell(record, column))}</tr>
  );
}

function renderCell<T extends Record>(record: T, column: Column<T>) {
  const content = column.renderCell?.(record, column) || format(record, column);
  if ((content as any)?.type === 'td') {
    return content;
  }

  return (
    <td
      className={clsx(column.classes)}
      key={column.id.toString()}
      style={getStyleFromColumn(column)}
    >
      {content}
    </td>
  );
}

export function format<T extends Record>(
  record: T,
  column: Column<T>,
): ReactNode {
  if (column.format) {
    if (typeof column.format === 'function') {
      return column.format(record, column);
    }

    const formatFunction = formats[column.format];
    if (typeof formatFunction === 'function') {
      return formatFunction(record, column as Column<any>);
    }
  }

  return record[column.id] as any;
}

function columnsFromConfig<T>(config: Column<T>[]): Column<T>[] {
  return config
    .filter((column) => column.visible !== false)
    .map((column) => {
      let normalizedClasses = column.classes || [];
      if (!Array.isArray(normalizedClasses)) {
        normalizedClasses = [normalizedClasses];
      }

      normalizedClasses.push(classes.cell);
      normalizedClasses = normalizedClasses.filter(Boolean);

      return { ...column, classes: normalizedClasses };
    });
}

function getStyleFromColumn(column: Column<any>) {
  const style: CSSProperties = {};

  for (const key of ['maxWidth', 'minWidth', 'textAlign', 'width'] as const) {
    if (column[key]) {
      style[key] = column[key] as any;
    }
  }

  return Object.keys(style).length === 0 ? undefined : style;
}
